import React, { FC } from 'react';
import { View, Text } from 'react-native';
import i18n from 'i18n-js';

import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import TouchableElement from '@components/common/touchableElement';
import StatusBadge from '@components/common/StatusBadge';
import JobThumbnail from '@v2/components/FinishJobModal/components/JobThumbnail';
import Separator from '@components/common/Separator';
import { getStatusBadgeScale, FINISH_JOB_THUMBNAIL_WIDTH } from '@v2/components/FinishJobModal/constants';
import useManageJobInfoSection from '@v2/components/FinishJobModal/hooks/useManageJobInfoSection';
import type { JobInfoSectionProps } from '@v2/components/FinishJobModal/types';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';

const JobInfoSection: FC<JobInfoSectionProps> = (props) => {
  const { C, item, scale = initialScale, font = initialFont } = props;
  const {
    jobConfig,
    productName,
    jobNumber,
    customerName,
    scheduledEnd,
    formattedActualEnd,
    imageProps,
    isImageLoading,
    openDatePicker,
  } = useManageJobInfoSection(props);

  return (
    <View style={{ paddingHorizontal: scale(24), paddingVertical: scale(16) }}>
      <View style={{ flexDirection: 'row', gap: scale(16) }}>
        {/* Image */}
        <View
          style={{
            width: scale(FINISH_JOB_THUMBNAIL_WIDTH),
            alignSelf: 'stretch',
            borderRadius: scale(SCREEN_BORDER_RADIUS),
            backgroundColor: C('Gray/Light/5'),
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
          }}
        >
          <JobThumbnail C={C} imageProps={imageProps} isImageLoading={isImageLoading} font={font} />
        </View>

        {/* Info column */}
        <View style={{ flex: 1, gap: scale(4) }}>
          {/* Product name */}
          <Text style={[font['Headline'], { color: C('Label/Light/Primary') }]} numberOfLines={2}>
            {productName}
          </Text>

          {/* Job number + Status badge */}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(8) }}>
            <Text style={[font['Subhead 1'], { color: C('Label/Light/Tertiary') }]}>{jobNumber}</Text>
            <StatusBadge isClosed={false} isInProgress={item.status === 'Running'} C={C} config={jobConfig} scale={getStatusBadgeScale(scale)} font={font} />
          </View>

          {/* Customer */}
          <View style={{ flexDirection: 'row', gap: scale(4) }}>
            <Text style={[font['Footnote'], { color: C('Label/Light/Tertiary') }]}>{i18n.t('Customer')}:</Text>
            <Text style={[font['Footnote'], { color: C('Label/Light/Secondary') }]}>{customerName}</Text>
          </View>

          {/* Scheduled End + Actual End on the same row */}
          <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: scale(4), columnGap: scale(80) }}>
            <View style={{ flexDirection: 'row', gap: scale(4) }}>
              <Text style={[font['Footnote'], { color: C('Label/Light/Tertiary') }]}>{i18n.t('Scheduled End')}:</Text>
              <Text style={[font['Footnote'], { color: C('Label/Light/Secondary') }]}>{scheduledEnd}</Text>
            </View>

            <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(4) }}>
              <Text style={[font['Footnote'], { color: C('Label/Light/Tertiary') }]}>{i18n.t('Actual End')}:</Text>
              <TouchableElement testID="finish-job-actual-end-date" onPress={openDatePicker}>
                {({ boxStyle }) => (
                  <View style={boxStyle}>
                    <Text style={[font['Footnote'], { color: C('Link/Light/Primary') }]}>{formattedActualEnd}</Text>
                  </View>
                )}
              </TouchableElement>
            </View>
          </View>
        </View>
      </View>

      <View style={{ marginTop: scale(16) }}>
        <Separator C={C} />
      </View>
    </View>
  );
};

export default JobInfoSection;
